import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PumpAnalysis } from "@shared/schema";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResultsRecommendation } from "./results-recommendation";
import { ResultsComparison } from "./results-comparison";
import { ResultsExport } from "./results-export";

interface ResultsDashboardProps {
  analysis: PumpAnalysis;
}

export function ResultsDashboard({ analysis }: ResultsDashboardProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("input-summary");
  
  if (!analysis.results) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <h2 className="text-xl font-semibold mb-2">{t("results.noResults")}</h2>
            <p className="text-muted-foreground">{t("results.noResultsDescription")}</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const results = analysis.results as any;
  const recommendations = results.recommendations || [];
  
  return (
    <Card className="mb-8">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">{t("results.title")}</h2>
          <ResultsExport analysis={analysis} />
        </div>
        
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="input-summary">{t("results.tabs.inputSummary")}</TabsTrigger>
            <TabsTrigger value="recommendations">{t("results.tabs.recommendations")}</TabsTrigger>
            <TabsTrigger value="compare">{t("results.tabs.compare")}</TabsTrigger>
            <TabsTrigger value="next-steps">{t("results.tabs.nextSteps")}</TabsTrigger>
          </TabsList>
          
          {/* Input Summary Tab */}
          <TabsContent value="input-summary" className="pt-6">
            <h3 className="text-lg font-medium mb-4">{t("results.inputSummary.title")}</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  <tr className="border-b">
                    <td colSpan={2} className="px-4 py-3 text-sm font-medium text-primary bg-muted">{t("results.inputSummary.pumpInfo")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground w-1/3">{t("form.manufacturer")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.manufacturer}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.model")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.model}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.age")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.age} {t("common.years")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.head")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.head} {t("common.ft")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.flow")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.flow} {t("common.gpm")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.hp")}</td>
                    <td className="px-4 py-3 text-sm">{analysis.hp} {t("common.hp")}</td>
                  </tr>
                  
                  <tr className="border-b">
                    <td colSpan={2} className="px-4 py-3 text-sm font-medium text-primary bg-muted">{t("results.inputSummary.appDetails")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.application")}</td>
                    <td className="px-4 py-3 text-sm">{t(`form.applicationOptions.${analysis.application}`)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.fluidType")}</td>
                    <td className="px-4 py-3 text-sm">{t(`form.fluidTypeOptions.${analysis.fluidType}`)}</td>
                  </tr>
                  
                  <tr className="border-b">
                    <td colSpan={2} className="px-4 py-3 text-sm font-medium text-primary bg-muted">{t("results.inputSummary.operationUtility")}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.pumpSchedule")}</td>
                    <td className="px-4 py-3 text-sm">{t(`form.scheduleOptions.${analysis.pumpSchedule}`)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.electricalSetup")}</td>
                    <td className="px-4 py-3 text-sm">{t(`form.electricalOptions.${analysis.electricalSetup}`)}</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.utilityRate")}</td>
                    <td className="px-4 py-3 text-sm">${analysis.utilityRate}/kWh</td>
                  </tr>
                  <tr className="border-b">
                    <td className="px-4 py-3 text-sm font-medium text-muted-foreground">{t("form.controlType")}</td>
                    <td className="px-4 py-3 text-sm">{t(`form.controlOptions.${analysis.controlType}`)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </TabsContent>
          
          {/* Recommendations Tab */}
          <TabsContent value="recommendations" className="pt-6">
            <ResultsRecommendation 
              analysis={analysis}
              recommendations={recommendations}
            />
          </TabsContent>
          
          {/* Comparison Tab */}
          <TabsContent value="compare" className="pt-6">
            <ResultsComparison 
              analysis={analysis}
              recommendations={recommendations}
              analyticsData={results.analysis}
            />
          </TabsContent>
          
          {/* Next Steps Tab */}
          <TabsContent value="next-steps" className="pt-6">
            <h3 className="text-lg font-medium mb-4">{t("results.nextSteps.title")}</h3>
            <p className="text-muted-foreground mb-4">{t("results.nextSteps.description")}</p>
            
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                <li>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary/10 rounded-full">
                        <span className="text-primary font-bold">1</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-sm font-medium">{t("results.nextSteps.contactManufacturer")}</h4>
                        <p className="text-sm text-muted-foreground">
                          {recommendations[0]?.manufacturer}: 800-879-6636 {t("results.nextSteps.orContact")}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary/10 rounded-full">
                        <span className="text-primary font-bold">2</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-sm font-medium">{t("results.nextSteps.scheduleAssessment")}</h4>
                        <p className="text-sm text-muted-foreground">{t("results.nextSteps.assessmentDescription")}</p>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary/10 rounded-full">
                        <span className="text-primary font-bold">3</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-sm font-medium">{t("results.nextSteps.requestIncentives")}</h4>
                        <p className="text-sm text-muted-foreground">{t("results.nextSteps.incentivesDescription")}</p>
                      </div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="px-4 py-4 sm:px-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-primary/10 rounded-full">
                        <span className="text-primary font-bold">4</span>
                      </div>
                      <div className="ml-4">
                        <h4 className="text-sm font-medium">{t("results.nextSteps.reviewTimeline")}</h4>
                        <p className="text-sm text-muted-foreground">{t("results.nextSteps.timelineDescription")}</p>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
